import { useQueryClient } from "@tanstack/react-query";
import { DraftReallocationAlert } from "features/capital-activity/DraftReallocationAlert";
import { CapitalEventBreakdownTable } from "features/capital-activity/event/CapitalEventBreakdownTable";
import { CapitalEventPageMenu } from "features/capital-activity/event/CapitalEventPageMenu";
import { CapitalEventStatsCard } from "features/capital-activity/event/CapitalEventStatsCard";
import { CapitalEventTable } from "features/capital-activity/event/CapitalEventTable";
import { getCapitalActivitySummaryRetrieveQueryKey } from "gen/endpoints/capital-activity/capital-activity";
import {
  useFundFamilyEventRetrieveSuspense,
  useFundFamilySubEventFirstRetrieve,
} from "gen/endpoints/fund-family-event/fund-family-event";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { OutOfDateAlert } from "shared/components/alert/OutOfDateAlert";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const CapitalEventReviewPage: React.FC = () => {
  const { t } = useTranslation(["common", "capitalActivity"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();

  const { data: activityData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const { data: waterfallSubeventData } = useFundFamilySubEventFirstRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      type: "waterfall",
      raise_if_none: false,
    },
  );

  const waterfallFundFamilySubEventId =
    waterfallSubeventData?.fund_family_subevent_data?.id;

  // NOTE: we will eventually shift this to be a refresh rather than a discard
  const onDiscard = async () => {
    await queryClient.invalidateQueries({
      queryKey: getCapitalActivitySummaryRetrieveQueryKey(fundFamilyId),
    });
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .capitalActivity({}).$,
    );
  };

  return (
    <PageContent>
      <PageHeader
        title={activityData.event_type_title}
        backButton={{
          title: t("common:capitalActivity"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .capitalActivity({}).$,
        }}
        stats={
          <CapitalEventStatsCard
            waterfallFundFamilySubEventId={waterfallFundFamilySubEventId!}
            capitalActivityData={activityData}
            isDraftEvent={false}
            fundFamilyEventId={fundFamilyEventId}
            fundFamilyId={fundFamilyId}
          />
        }
        actions={
          <CapitalEventPageMenu
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={fundFamilyEventId}
            activityData={activityData}
          />
        }
      />
      <PageBody>
        {!!activityData.out_of_date_reason && (
          <OutOfDateAlert
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={fundFamilyEventId}
            message={activityData.out_of_date_reason}
            onDiscard={onDiscard}
          />
        )}
        <DraftReallocationAlert
          fundFamilyId={fundFamilyId}
          fundFamilyEventId={fundFamilyEventId}
        />
        <CapitalEventTable
          fundFamilyId={fundFamilyId}
          fundFamilyEventId={fundFamilyEventId}
          isFullWidth={true}
          isReview={true}
        />
        <CapitalEventBreakdownTable
          fundFamilyId={fundFamilyId}
          fundFamilyEventId={fundFamilyEventId}
        />
      </PageBody>
    </PageContent>
  );
};
